/**
 * Country coordinates for map markers
 *
 * Maps normalized country names to their approximate geographic centers (capital cities or major cities)
 * Used for placing content markers on the map to show where films/titles originate.
 */

export interface CountryCoordinates {
  /** Longitude (West-East, -180 to 180) */
  lng: number;
  /** Latitude (South-North, -90 to 90) */
  lat: number;
}

/**
 * Map of normalized country names to their geographic coordinates
 * Coordinates are approximate centers (usually capital cities)
 */
export const COUNTRY_COORDINATES: Record<string, CountryCoordinates> = {
  // Americas
  "United States of America": { lng: -95.7129, lat: 37.902 },
  Canada: { lng: -106.3468, lat: 56.1304 },
  Mexico: { lng: -102.5528, lat: 23.6345 },
  Brazil: { lng: -47.8825, lat: -15.7975 },
  Argentina: { lng: -63.6167, lat: -38.4161 },
  Chile: { lng: -71.543, lat: -35.6751 },
  Colombia: { lng: -74.2973, lat: 4.5709 },
  Peru: { lng: -75.152, lat: -9.19 },
  Venezuela: { lng: -66.5897, lat: 6.4238 },
  Ecuador: { lng: -78.1834, lat: -1.8312 },
  Uruguay: { lng: -55.7658, lat: -32.5228 },
  Paraguay: { lng: -58.4438, lat: -23.4425 },
  Bolivia: { lng: -63.5887, lat: -16.2902 },
  "Costa Rica": { lng: -83.7534, lat: 9.7489 },
  Panama: { lng: -80.7821, lat: 8.538 },
  Cuba: { lng: -77.7812, lat: 21.5218 },
  "Dominican Republic": { lng: -70.1627, lat: 18.7357 },
  Guatemala: { lng: -90.2308, lat: 15.7835 },
  Honduras: { lng: -86.2419, lat: 15.2 },
  "El Salvador": { lng: -88.8965, lat: 13.7942 },
  Nicaragua: { lng: -85.2072, lat: 12.8654 },
  Jamaica: { lng: -77.2975, lat: 18.1096 },
  "Trinidad and Tobago": { lng: -61.2225, lat: 10.6918 },

  // Europe - Western
  "United Kingdom": { lng: -3.4359, lat: 55.3781 },
  France: { lng: 2.2137, lat: 46.2276 },
  Germany: { lng: 10.4515, lat: 51.1657 },
  Italy: { lng: 12.5674, lat: 41.8719 },
  Spain: { lng: -3.7492, lat: 40.4637 },
  Portugal: { lng: -8.2245, lat: 39.3999 },
  Netherlands: { lng: 5.2913, lat: 52.1326 },
  Belgium: { lng: 4.4699, lat: 50.5039 },
  Switzerland: { lng: 8.2275, lat: 46.8182 },
  Austria: { lng: 14.5501, lat: 47.5162 },
  Ireland: { lng: -8.2439, lat: 53.4129 },
  Luxembourg: { lng: 6.1296, lat: 49.8153 },

  // Europe - Nordic
  Sweden: { lng: 18.6435, lat: 60.1282 },
  Norway: { lng: 8.4689, lat: 60.472 },
  Denmark: { lng: 9.5018, lat: 56.2639 },
  Finland: { lng: 25.7482, lat: 61.9241 },
  Iceland: { lng: -19.208, lat: 64.9631 },

  // Europe - Eastern
  Poland: { lng: 19.1451, lat: 51.9194 },
  Czechia: { lng: 15.473, lat: 49.8175 },
  Slovakia: { lng: 19.699, lat: 48.669 },
  Hungary: { lng: 19.5033, lat: 47.1625 },
  Romania: { lng: 24.9668, lat: 45.9432 },
  Bulgaria: { lng: 25.4858, lat: 42.7339 },
  Greece: { lng: 21.8243, lat: 39.742 },
  Croatia: { lng: 15.2, lat: 45.1 },
  Slovenia: { lng: 14.9955, lat: 46.1512 },
  Serbia: { lng: 21.059, lat: 44.165 },
  Lithuania: { lng: 23.8813, lat: 55.1694 },
  Latvia: { lng: 24.6032, lat: 56.8796 },
  Estonia: { lng: 25.136, lat: 58.5953 },
  Malta: { lng: 14.3754, lat: 35.9375 },
  Cyprus: { lng: 33.4299, lat: 35.1264 },

  // Europe - Eastern & Russia
  Russia: { lng: 105.3188, lat: 61.524 },
  Ukraine: { lng: 31.1656, lat: 48.3794 },
  Belarus: { lng: 27.9534, lat: 53.7098 },
  Moldova: { lng: 28.3699, lat: 47.4116 },

  // Asia - East
  China: { lng: 104.1954, lat: 35.8617 },
  Japan: { lng: 138.2529, lat: 36.2048 },
  "South Korea": { lng: 127.7669, lat: 35.9078 },
  Taiwan: { lng: 120.9605, lat: 23.6978 },
  "Hong Kong": { lng: 114.1095, lat: 22.3193 },
  Mongolia: { lng: 103.8467, lat: 46.8625 },

  // Asia - Southeast
  Thailand: { lng: 100.9925, lat: 15.87 },
  Vietnam: { lng: 108.2772, lat: 14.583 },
  Philippines: { lng: 121.774, lat: 12.8797 },
  Indonesia: { lng: 113.9213, lat: -0.7893 },
  Malaysia: { lng: 101.9758, lat: 4.2105 },
  Singapore: { lng: 103.8198, lat: 1.3521 },
  Myanmar: { lng: 95.956, lat: 21.9162 },
  Cambodia: { lng: 104.991, lat: 12.5657 },
  Laos: { lng: 102.4955, lat: 19.8563 },

  // Asia - South
  India: { lng: 78.9629, lat: 20.5937 },
  Pakistan: { lng: 69.3451, lat: 30.3753 },
  Bangladesh: { lng: 90.3563, lat: 23.685 },
  "Sri Lanka": { lng: 80.7718, lat: 7.8731 },
  Nepal: { lng: 84.124, lat: 28.3949 },

  // Middle East
  Turkey: { lng: 35.2433, lat: 38.9637 },
  Iran: { lng: 53.688, lat: 32.4279 },
  Iraq: { lng: 43.6793, lat: 33.2232 },
  "Saudi Arabia": { lng: 45.792, lat: 23.8859 },
  "United Arab Emirates": { lng: 53.8478, lat: 23.4241 },
  Israel: { lng: 34.8516, lat: 31.461 },
  Jordan: { lng: 36.2384, lat: 30.5852 },
  Lebanon: { lng: 35.8623, lat: 33.8547 },
  Syria: { lng: 38.9968, lat: 34.8021 },
  Yemen: { lng: 48.5164, lat: 15.5527 },
  Oman: { lng: 55.9233, lat: 21.4735 },
  Kuwait: { lng: 47.4818, lat: 29.3117 },
  Qatar: { lng: 51.1839, lat: 25.3548 },
  Bahrain: { lng: 50.5577, lat: 26.667 },

  // Africa
  Egypt: { lng: 30.8025, lat: 26.8206 },
  "South Africa": { lng: 22.9375, lat: -30.5595 },
  Nigeria: { lng: 8.6753, lat: 9.82 },
  Kenya: { lng: 37.9062, lat: -0.236 },
  Ethiopia: { lng: 40.4897, lat: 9.145 },
  Ghana: { lng: -1.232, lat: 7.9465 },
  Tanzania: { lng: 34.8888, lat: -6.369 },
  Uganda: { lng: 32.2903, lat: 1.3733 },
  Morocco: { lng: -7.926, lat: 31.7917 },
  Algeria: { lng: 1.6596, lat: 28.339 },
  Tunisia: { lng: 9.5375, lat: 33.8869 },
  Senegal: { lng: -14.4524, lat: 14.4974 },
  "Ivory Coast": { lng: -5.5471, lat: 7.54 },
  Cameroon: { lng: 12.3547, lat: 7.3697 },
  Zimbabwe: { lng: 29.1549, lat: -19.154 },

  // Oceania
  Australia: { lng: 133.7751, lat: -25.2744 },
  "New Zealand": { lng: 174.886, lat: -40.9006 },
};

/**
 * Get coordinates for a normalized country name
 * Returns null if country not found
 */
export function getCountryCoordinates(countryName: string): CountryCoordinates | null {
  return COUNTRY_COORDINATES[countryName] || null;
}

/**
 * Check if coordinates are available for a country
 */
export function hasCountryCoordinates(countryName: string): boolean {
  return countryName in COUNTRY_COORDINATES;
}
